import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Handshake, Users, Building2, Landmark, ArrowRight, ArrowLeft, Sparkles, Loader2 } from "lucide-react";

const CONFLICTS = [
  { id: "supplier", icon: Handshake, c: "#8B5CF6", key: "form.supplier" },
  { id: "union", icon: Users, c: "#F59E0B", key: "form.union" },
  { id: "rent", icon: Building2, c: "#EAB308", key: "form.rent" },
  { id: "tax", icon: Landmark, c: "#3B82F6", key: "form.tax" },
];

export default function NewSimulation() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ sector: "", conflict_type: "supplier", context_notes: "", revenue: "", ebitda: "", contested_amount: "" });
  const [generating, setGenerating] = useState(false);
  const [streamText, setStreamText] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setGenerating(true);
    setStreamText("");
    const payload = {
      sector: form.sector || (i18n.language === "en" ? "General" : "Général"),
      conflict_type: form.conflict_type,
      revenue: form.revenue ? parseFloat(form.revenue) : null,
      ebitda: form.ebitda ? parseFloat(form.ebitda) : null,
      contested_amount: form.contested_amount ? parseFloat(form.contested_amount) : null,
      context_notes: form.context_notes || null,
      language: i18n.language === "en" ? "en" : "fr",
    };

    try {
      const res = await fetch(`${API}/simulations/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let simId = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const json = JSON.parse(line.slice(5).trim());
          if (json.type === "delta") {
            setStreamText((prev) => prev + json.content);
          } else if (json.type === "done") {
            simId = json.simulation.id;
          } else if (json.type === "error") {
            throw new Error(json.message);
          }
        }
      }
      if (simId) {
        navigate(`/simulation/${simId}`, { replace: true });
      } else {
        throw new Error("no simulation id");
      }
    } catch (e) {
      setGenerating(false);
      toast.error(t("result.error"));
    }
  };

  if (generating) {
    return (
      <div className="min-h-screen bg-[#0B0E14]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg bg-primary/15 border border-primary/40 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary animate-pulse" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl">{t("result.streaming")}</h2>
              <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("result.generating")}</p>
            </div>
          </div>
          <div className="rounded-xl bg-[#0B0E14] border border-border p-6 font-mono-fin text-sm text-[#9CA3AF] leading-relaxed whitespace-pre-wrap min-h-[300px]" data-testid="stream-output">
            {streamText}
            <span className="inline-block w-2 h-4 bg-primary ml-0.5 animate-pulse align-middle" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl tracking-tight">{t("form.title")}</h1>
          <div className="flex items-center gap-3 mt-4">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-mono-fin border ${step >= s ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`}>{s}</div>
                <span className={`text-sm ${step >= s ? "text-foreground" : "text-muted-foreground"}`}>{s === 1 ? t("form.step1") : t("form.step2")}</span>
                {s === 1 && <div className="w-10 h-px bg-border" />}
              </div>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="rounded-xl bg-[#151A22] border border-border p-7 space-y-7">
              <div>
                <Label className="text-sm mb-2 block">{t("form.sector")}</Label>
                <Input data-testid="input-sector" value={form.sector} onChange={(e) => update("sector", e.target.value)} placeholder={t("form.sector_ph")}
                  className="bg-[#0B0E14] border-border focus-visible:ring-primary" />
              </div>
              <div>
                <Label className="text-sm mb-3 block">{t("form.conflict")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  {CONFLICTS.map((c) => {
                    const Icon = c.icon; const active = form.conflict_type === c.id;
                    return (
                      <button key={c.id} data-testid={`conflict-${c.id}`} onClick={() => update("conflict_type", c.id)}
                        className={`flex items-center gap-3 rounded-lg border p-4 transition-all ${active ? "border-primary bg-primary/10" : "border-border bg-[#0B0E14] hover:border-[#3a4456]"}`}>
                        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${c.c}1f`, border: `1px solid ${c.c}55` }}>
                          <Icon className="h-4.5 w-4.5" style={{ color: c.c }} />
                        </div>
                        <span className="font-medium">{t(c.key)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-sm mb-2 block">{t("form.notes")}</Label>
                <Textarea data-testid="input-notes" value={form.context_notes} onChange={(e) => update("context_notes", e.target.value)} placeholder={t("form.notes_ph")}
                  className="bg-[#0B0E14] border-border focus-visible:ring-primary min-h-[90px]" />
              </div>
              <div className="flex justify-end">
                <Button data-testid="step1-next-btn" onClick={() => setStep(2)} disabled={!form.sector}
                  className="bg-primary text-white hover:bg-[#6D28D9] rounded-lg">
                  {t("form.next")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="rounded-xl bg-[#151A22] border border-border p-7 space-y-6">
              <p className="text-sm text-muted-foreground">{t("form.optional_hint")}</p>
              <div>
                <Label className="text-sm mb-2 block">{t("form.revenue")}</Label>
                <Input data-testid="input-revenue" type="number" value={form.revenue} onChange={(e) => update("revenue", e.target.value)} placeholder="5 000 000"
                  className="bg-[#0B0E14] border-border focus-visible:ring-primary font-mono-fin" />
              </div>
              <div>
                <Label className="text-sm mb-2 block">{t("form.ebitda")}</Label>
                <Input data-testid="input-ebitda" type="number" value={form.ebitda} onChange={(e) => update("ebitda", e.target.value)} placeholder="600 000"
                  className="bg-[#0B0E14] border-border focus-visible:ring-primary font-mono-fin" />
              </div>
              <div>
                <Label className="text-sm mb-2 block">{t("form.contested")}</Label>
                <Input data-testid="input-contested" type="number" value={form.contested_amount} onChange={(e) => update("contested_amount", e.target.value)} placeholder="120 000"
                  className="bg-[#0B0E14] border-border focus-visible:ring-primary font-mono-fin" />
              </div>
              <div className="flex items-center justify-between pt-2">
                <Button data-testid="step2-back-btn" variant="outline" onClick={() => setStep(1)}
                  className="bg-transparent border-border text-foreground hover:bg-[#1E2532] hover:text-white rounded-lg">
                  <ArrowLeft className="mr-2 h-4 w-4" /> {t("form.back")}
                </Button>
                <Button data-testid="generate-btn" onClick={generate}
                  className="bg-primary text-white hover:bg-[#6D28D9] rounded-lg shadow-[0_0_15px_rgba(124,58,237,0.3)]">
                  <Sparkles className="mr-2 h-4 w-4" /> {t("form.generate")}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
